import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '@/config/api';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Plus, Building2 } from 'lucide-react';
import { toast } from 'sonner';

const API = API_BASE_URL;

const Companies = () => {
  const { t } = useTranslation();
  const [companies, setCompanies] = useState([]);
  const [openAdd, setOpenAdd] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '', subscription_plan: 'trial', subscription_status: 'active' });

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const { data } = await axios.get(`${API}/companies`, { withCredentials: true });
      setCompanies(data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleAdd = async () => {
    try {
      await axios.post(`${API}/companies`, form, { withCredentials: true });
      toast.success('Company added');
      setOpenAdd(false);
      setForm({ name: '', email: '', phone: '', address: '', subscription_plan: 'trial', subscription_status: 'active' });
      fetchCompanies();
    } catch (error) {
      toast.error('Failed to add');
    }
  };

  return (
    <div className="space-y-6" data-testid="companies-page">
      <div className="flex items-center justify-between">
        <h1 className="text-4xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', color: '#232D4B' }}>
          {t('companies')}
        </h1>
        <Dialog open={openAdd} onOpenChange={setOpenAdd}>
          <DialogTrigger asChild>
            <Button style={{ backgroundColor: '#E57200' }} data-testid="add-company-btn">
              <Plus className="h-4 w-4 me-2" /> Add Company
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Company</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div><Label>Company Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="company-name" /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="company-email" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="company-phone" /></div>
              <div><Label>Address</Label><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="company-address" /></div>
            </div>
            <DialogFooter>
              <Button onClick={handleAdd} style={{ backgroundColor: '#E57200' }} data-testid="save-company-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-border/40">
        <CardContent className="pt-6">
          <Table data-testid="companies-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Plan</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {companies.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground">No companies yet</TableCell></TableRow>
              ) : (
                companies.map(c => (
                  <TableRow key={c.id} className="hover:bg-muted/50">
                    <TableCell className="font-medium flex items-center gap-2">
                      <Building2 className="h-4 w-4" style={{ color: '#E57200' }} />
                      {c.name}
                    </TableCell>
                    <TableCell>{c.email}</TableCell>
                    <TableCell>{c.phone}</TableCell>
                    <TableCell><Badge style={{ backgroundColor: '#232D4B', color: 'white' }}>{c.subscription_plan}</Badge></TableCell>
                    <TableCell><Badge style={{ backgroundColor: c.subscription_status === 'active' ? '#10B981' : '#EF4444', color: 'white' }}>{c.subscription_status}</Badge></TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default Companies;
